import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { MapPin, Plus, Building2, Key, Copy, Check, Pencil, X } from 'lucide-react';
import {
  PageHeader, Card, Alert, EmptyState, Btn, FieldLabel, Input, Badge, CloudComingSoonAlert,
  Select, dash, cn,
} from './dashboard/DashboardUI';
import {
  RENEWAL_PRICE,
  calcBranchAddTotal,
  formatBranchAddBreakdown,
} from '../utils/pricing';
import {
  branchHasActiveCounterPc,
  branchHasCounterPcSlotUsed,
  getCounterPcForBranch,
} from '../utils/devices';
import { trackBranchPayment } from '../gtag';

const emptyForm = { name: '', code: '', address: '', city: '', phone: '' };

/** Submit a classic HTML form POST to PayU (hosted / merchant form checkout). */
function submitPayuForm(paymentUrl, formFields) {
  const form = document.createElement('form');
  form.method = 'POST';
  form.action = paymentUrl;
  form.style.display = 'none';
  Object.entries(formFields || {}).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = key;
    input.value = String(value);
    form.appendChild(input);
  });
  document.body.appendChild(form);
  form.submit();
}

export default function BranchesPanel({
  branches,
  devices = [],
  restaurant,
  onRefresh,
  onError,
  onClearError,
  onInvoicesRefresh,
}) {
  const [showForm, setShowForm] = useState(false);
  const [formStep, setFormStep] = useState('details');
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [copiedKey, setCopiedKey] = useState('');
  const [form, setForm] = useState(emptyForm);
  const [purchaseYears, setPurchaseYears] = useState(1);
  const [paymentNote, setPaymentNote] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();
  const verifyingRef = useRef(false);

  const authHeaders = () => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${localStorage.getItem('token')}`,
  });

  // After PayU redirect: ?tab=branches&payment=return|failed&order_id=...
  useEffect(() => {
    const payment = searchParams.get('payment');
    const orderId = searchParams.get('order_id');
    if (!payment || !orderId || verifyingRef.current) return;
    if (payment !== 'return' && payment !== 'failed') return;

    verifyingRef.current = true;
    (async () => {
      setSaving(true);
      onClearError?.();
      try {
        if (payment === 'failed') {
          onError?.('Payment was cancelled or failed. You can try again.');
          setPaymentNote('');
          return;
        }
        setPaymentNote('Confirming PayU payment…');
        const res = await fetch(`${process.env.REACT_APP_API_URL}/payment/branch/verify`, {
          method: 'POST',
          headers: authHeaders(),
          body: JSON.stringify({ orderId }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Payment verification failed');
        if (!data.alreadyCompleted) {
          trackBranchPayment({ orderId, amount: purchaseTotal });
        }
        setPaymentNote(
          data.alreadyCompleted
            ? 'Branch already created for this payment.'
            : `Payment successful — branch "${data.branch?.name || ''}" is ready.`,
        );
        resetForm();
        await onRefresh?.();
        await onInvoicesRefresh?.();
      } catch (err) {
        onError?.(err.message);
        setPaymentNote('');
      } finally {
        setSaving(false);
        const next = new URLSearchParams(searchParams);
        next.delete('payment');
        next.delete('order_id');
        next.delete('reason');
        setSearchParams(next, { replace: true });
        verifyingRef.current = false;
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const activeBranches = branches.filter((b) => b.status === 'active');
  const maxBranches = restaurant?.maxBranches || 1;
  const isCloudPlan = restaurant?.plan === 'cloud';
  const canAddMore = isCloudPlan || activeBranches.length < maxBranches;
  const needsPurchase = !isCloudPlan && activeBranches.length >= maxBranches;

  const purchaseTotal = calcBranchAddTotal({ selectedYears: purchaseYears });
  const purchaseBreakdown = formatBranchAddBreakdown({ selectedYears: purchaseYears });

  const resetForm = () => {
    setForm(emptyForm);
    setShowForm(false);
    setFormStep('details');
    setEditingId(null);
    setPurchaseYears(1);
  };

  const copyKey = async (key, id) => {
    try {
      await navigator.clipboard.writeText(key);
      setCopiedKey(id);
      setTimeout(() => setCopiedKey(''), 2000);
    } catch {
      onError?.('Failed to copy sync key');
    }
  };

  const startEdit = (branch) => {
    onClearError?.();
    setEditingId(branch._id);
    setShowForm(false);
    setForm({
      name: branch.name || '',
      code: branch.code || '',
      address: branch.address || '',
      city: branch.city || '',
      phone: branch.phone || '',
    });
  };

  const submitBranch = async (purchaseAmount = 0) => {
    setSaving(true);
    onClearError?.();
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/branches`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          ...form,
          purchaseAmount,
          purchaseYears,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to create branch');
      resetForm();
      await onRefresh?.();
      await onInvoicesRefresh?.();
    } catch (err) {
      onError?.(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      onError?.('Branch name is required');
      return;
    }
    if (needsPurchase) {
      setFormStep('payment');
      return;
    }
    await submitBranch(0);
  };

  const handlePurchaseAndCreate = async () => {
    setSaving(true);
    onClearError?.();
    setPaymentNote('');
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/payment/branch/create-order`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          ...form,
          purchaseYears,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Could not start payment');
      if (!data.paymentUrl || !data.form) {
        throw new Error('PayU payment form was not returned by the server');
      }
      setPaymentNote('Redirecting to PayU secure checkout…');
      // Full-page POST to PayU; browser returns to surl/furl → dashboard
      submitPayuForm(data.paymentUrl, data.form);
    } catch (err) {
      onError?.(err.message);
      setSaving(false);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      onError?.('Branch name is required');
      return;
    }
    setSaving(true);
    onClearError?.();
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/branches/${editingId}`,
        {
          method: 'PUT',
          headers: authHeaders(),
          body: JSON.stringify(form),
        },
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to update branch');
      resetForm();
      await onRefresh?.();
    } catch (err) {
      onError?.(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async (branch) => {
    if (branch.isDefault) {
      onError?.('Cannot deactivate the default branch');
      return;
    }
    if (!window.confirm(`Deactivate branch "${branch.name}"?`)) return;
    onClearError?.();
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/branches/${branch._id}`,
        { method: 'DELETE', headers: authHeaders() },
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to deactivate branch');
      await onRefresh?.();
    } catch (err) {
      onError?.(err.message);
    }
  };

  const renderPaymentStep = () => (
    <div className={cn(dash.card, dash.cardPad, 'space-y-4 ring-1 ring-indigo-500/20')}>
      <div>
        <h3 className="text-white font-semibold text-lg">Add branch — payment</h3>
        <p className="text-slate-400 text-sm mt-1">
          Standard plan includes one location. Each additional branch is ₹{RENEWAL_PRICE.toLocaleString('en-IN')}/year
          and gets its own LAN sync key.
        </p>
      </div>

      <div className={cn(dash.nested, 'p-4')}>
        <p className="text-white font-medium">{form.name}</p>
        <p className="text-slate-400 text-sm mt-1">{form.city || form.code || 'New location'}</p>
      </div>

      <div>
        <FieldLabel>Subscription period</FieldLabel>
        <Select
          value={purchaseYears}
          onChange={(e) => setPurchaseYears(parseInt(e.target.value, 10))}
        >
          <option value={1}>1 Year — ₹{RENEWAL_PRICE.toLocaleString('en-IN')}</option>
          <option value={2}>2 Years — ₹{(RENEWAL_PRICE * 2).toLocaleString('en-IN')}</option>
          <option value={3}>3 Years — ₹{(RENEWAL_PRICE * 3).toLocaleString('en-IN')}</option>
          <option value={5}>5 Years — ₹{(RENEWAL_PRICE * 5).toLocaleString('en-IN')}</option>
        </Select>
      </div>

      <div className={cn(dash.nested, 'p-4')}>
        <div className="flex justify-between text-sm">
          <span className="text-slate-400">{purchaseBreakdown.label}</span>
          <span className="text-white font-semibold">{purchaseBreakdown.amount}</span>
        </div>
        <p className="text-slate-500 text-xs mt-2 pt-2 border-t border-slate-800">
          Total due today: <strong className="text-white">₹{purchaseTotal.toLocaleString('en-IN')}</strong>
        </p>
      </div>

      <div className={cn(dash.nested, 'p-3 text-sm text-slate-300')}>
        Secure checkout via <strong className="text-white">PayU</strong>
        {' '}(UPI, cards, net banking). You will leave briefly for PayU and return here.
        The branch is created only after payment succeeds.
      </div>

      <div className="flex flex-wrap gap-3">
        <Btn onClick={handlePurchaseAndCreate} disabled={saving}>
          {saving ? 'Opening PayU…' : `Pay ₹${purchaseTotal.toLocaleString('en-IN')} with PayU`}
        </Btn>
        <Btn variant="secondary" onClick={() => setFormStep('details')}>
          Back
        </Btn>
      </div>
    </div>
  );

  const renderForm = (isEdit) => (
    <form
      onSubmit={isEdit ? handleUpdate : handleCreate}
      className={cn(dash.card, dash.cardPad, 'grid grid-cols-1 md:grid-cols-2 gap-4 ring-1 ring-indigo-500/20 min-w-0')}
    >
      <div className="md:col-span-2">
        <h3 className="text-white font-semibold text-lg">
          {isEdit ? 'Edit branch' : 'Add new branch'}
        </h3>
        <p className="text-slate-400 text-sm mt-1">
          Each branch gets its own LAN sync key and one counter PC. Billing is per branch, not per device.
        </p>
      </div>
      <div>
        <FieldLabel>Branch name *</FieldLabel>
        <Input
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          placeholder="e.g. MG Road"
          required
        />
      </div>
      <div>
        <FieldLabel>Branch code</FieldLabel>
        <Input
          value={form.code}
          onChange={(e) => setForm({ ...form, code: e.target.value })}
          placeholder="BR-02"
        />
      </div>
      <div>
        <FieldLabel>City</FieldLabel>
        <Input
          value={form.city}
          onChange={(e) => setForm({ ...form, city: e.target.value })}
        />
      </div>
      <div>
        <FieldLabel>Phone</FieldLabel>
        <Input
          value={form.phone}
          onChange={(e) => setForm({ ...form, phone: e.target.value })}
        />
      </div>
      <div className="md:col-span-2">
        <FieldLabel>Address</FieldLabel>
        <Input
          value={form.address}
          onChange={(e) => setForm({ ...form, address: e.target.value })}
        />
      </div>
      <div className="md:col-span-2 flex flex-wrap gap-3">
        <Btn type="submit" disabled={saving}>
          {saving
            ? 'Saving...'
            : isEdit
              ? 'Save changes'
              : needsPurchase
                ? 'Continue to payment'
                : 'Create branch'}
        </Btn>
        <Btn variant="secondary" type="button" onClick={resetForm}>
          Cancel
        </Btn>
      </div>
    </form>
  );

  return (
    <div className={dash.page}>
      <PageHeader
        overline="Locations"
        title="Branches"
        subtitle="Each branch is one shop location with one counter PC and its own sync key. Add branches at ₹1,999/year each."
        action={
          <Btn
            onClick={() => {
              onClearError?.();
              setEditingId(null);
              setForm(emptyForm);
              setFormStep('details');
              setShowForm((v) => !v);
            }}
          >
            <Plus className="w-4 h-4" />
            Add branch
          </Btn>
        }
      />

      <CloudComingSoonAlert />

      {paymentNote && (
        <Alert variant="success">{paymentNote}</Alert>
      )}

      <Alert variant="info">
        <span>
          Plan: <span className="text-indigo-400 font-semibold capitalize">{restaurant?.plan || 'standard'}</span>
          {' · '}
          Active branches: {activeBranches.length}
          {' / '}
          {isCloudPlan ? 'unlimited' : maxBranches}
        </span>
        {needsPurchase && (
          <p className="text-slate-300 mt-2 text-sm leading-relaxed">
            Your Main branch is included on Standard. Additional locations are <strong>₹{RENEWAL_PRICE.toLocaleString('en-IN')}/year</strong> each.
            Each branch gets one counter PC registration (once only). Add a branch for each new shop location.
          </p>
        )}
      </Alert>

      {showForm && !editingId && formStep === 'payment' && renderPaymentStep()}
      {showForm && !editingId && formStep === 'details' && renderForm(false)}
      {editingId && renderForm(true)}

      {branches.length === 0 ? (
        <EmptyState
          icon={Building2}
          title="No branches yet"
          description="Your main branch is created automatically on signup. Add another location when you open a new shop."
          action={
            <Btn onClick={() => setShowForm(true)}>
              <Plus className="w-4 h-4" />
              Add branch
            </Btn>
          }
        />
      ) : (
        <div className={dash.grid2}>
          {branches.map((branch) => {
            const linkedPc = getCounterPcForBranch(branch._id, devices);
            const slotUsed = branchHasCounterPcSlotUsed(branch._id, devices, branches);
            const hasActivePc = branchHasActiveCounterPc(branch._id, devices);

            return (
            <Card key={branch._id} hover>
              <div className="flex items-start justify-between gap-3 mb-4">
                <div className="flex items-center gap-3 min-w-0">
                  <div className={dash.iconBox}>
                    <Building2 className="w-5 h-5 text-white" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-white font-semibold truncate">{branch.name}</h3>
                    <p className="text-slate-400 text-sm">
                      {branch.code || '—'}
                      {branch.isDefault ? ' · Default' : ''}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  {branch.status === 'active' && (
                    <Badge variant={hasActivePc ? 'success' : slotUsed ? 'warning' : 'info'}>
                      {hasActivePc ? 'PC active' : slotUsed ? 'Slot used' : 'No PC yet'}
                    </Badge>
                  )}
                  <Badge variant={branch.status === 'active' ? 'success' : 'warning'}>
                    {branch.status}
                  </Badge>
                  {branch.status === 'active' && (
                    <button
                      type="button"
                      onClick={() => startEdit(branch)}
                      className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
                      title="Edit branch"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                  )}
                  {branch.status === 'active' && !branch.isDefault && (
                    <button
                      type="button"
                      onClick={() => handleDeactivate(branch)}
                      className="p-2 rounded-lg text-slate-400 hover:text-red-400 hover:bg-slate-800 transition-colors"
                      title="Deactivate branch"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>

              {(branch.address || branch.city) && (
                <div className="flex items-start gap-2 text-slate-400 text-sm mb-4">
                  <MapPin className="w-4 h-4 mt-0.5 shrink-0" />
                  <span>{[branch.address, branch.city].filter(Boolean).join(', ')}</span>
                </div>
              )}

              {linkedPc && (
                <p className="text-slate-400 text-sm mb-4">
                  Counter PC: <span className="text-white">{linkedPc.name || 'Counter PC'}</span>
                  {!hasActivePc && slotUsed && (
                    <span className="text-amber-400"> · access revoked</span>
                  )}
                </p>
              )}

              <div className={cn(dash.nested, 'p-3')}>
                <div className="flex items-center gap-2 text-slate-500 text-xs mb-2">
                  <Key className="w-3.5 h-3.5" />
                  Branch sync key (for this location)
                </div>
                <div className="flex items-center gap-2">
                  <code className="text-emerald-400 text-xs break-all flex-1 font-mono">
                    {branch.syncLicenseKey}
                  </code>
                  <button
                    type="button"
                    onClick={() => copyKey(branch.syncLicenseKey, branch._id)}
                    className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors shrink-0"
                  >
                    {copiedKey === branch._id ? (
                      <Check className="w-4 h-4 text-emerald-400" />
                    ) : (
                      <Copy className="w-4 h-4" />
                    )}
                  </button>
                </div>
              </div>
            </Card>
          );
          })}
        </div>
      )}
    </div>
  );
}